import { isApiError } from '@pa/api'
import { useGetAppointment } from '@pa/api/clinic'
import { Link } from '@tanstack/react-router'
import { Avatar, CloseButton, Drawer, fmtDateTimeOf, timeAgo } from '@pa/ui'
import { ExternalLink, ShieldCheck, TriangleAlert } from 'lucide-react'
import { useId, useState } from 'react'
import { OPEN_IN_PF, OPENING, useOpenInPf } from '../model/openInPf'
import { CONFLICTS_NOTE, CONFLICTS_TITLE, conflictLine, hasConflicts } from '../model/conflicts'
import { PF_MESSAGE_ROW, pfMessageLabel } from '../model/pfMessage'
import { pfRef, SourceName, TypeIcon, whenLine } from './tables'

/**
 * C-01a (UserFlows C-01a), the mockup's `AppointmentDrawer`: the header (type, "{day} · start–end", "PF appointment
 * ID: PF-xxxx", Open in Practice Fusion), Appointment (provider, location, duration, coverage), How it was booked
 * (booked time, source, and task 24's PF message status), task 26's "Conflicts at booking" (only when there are
 * some) and the privacy note. No cancel, reschedule or edit. #19 per id; #71 on Open in Practice
 * Fusion (task 23), whose failure shows inline under the button.
 */
export function AppointmentDrawer({
  clinicId,
  appointmentId,
  timeZone,
  onClose,
}: {
  clinicId: string
  appointmentId: string
  timeZone: string
  onClose: () => void
}) {
  const titleId = useId()
  const q = useGetAppointment(clinicId, appointmentId)
  const a = q.data
  const missing = q.isError && isApiError(q.error) && q.error.code === 'not_found'
  const [pfError, setPfError] = useState<string | null>(null)
  const pf = useOpenInPf(clinicId, setPfError)
  const opening = pf.opening === appointmentId

  return (
    <Drawer onClose={onClose} labelledBy={titleId}>
      {/* One header for every state, so the close button (and the focus on it) survives the data arriving. */}
      <div className="drawer-head">
        {a && (
          <span
            className="kpi-icon"
            style={{ width: 48, height: 48, borderRadius: 14, background: 'var(--blue-50)', color: 'var(--blue)' }}
            aria-hidden
          >
            <TypeIcon telehealth={a.visitType.telehealth} size={22} />
          </span>
        )}
        <div>
          <h2 id={titleId} style={{ fontSize: 18 }}>
            {a
              ? a.visitType.name
              : missing
                ? 'This appointment no longer exists'
                : q.isError
                  ? 'Couldn’t load this appointment'
                  : 'Loading…'}
          </h2>
          {a && (
            <>
              <div className="muted" style={{ fontSize: 13 }}>
                {whenLine(a, timeZone)}
              </div>
              <div style={{ marginTop: 8 }}>
                <span className="pf-ref">
                  <ShieldCheck aria-hidden /> PF appointment ID: {pfRef(a)}
                </span>
              </div>
              <div className="pf-open-row">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={opening}
                  onClick={() => {
                    setPfError(null)
                    pf.open(appointmentId)
                  }}
                >
                  {opening ? <span className="spinner" aria-hidden /> : <ExternalLink aria-hidden />}
                  {opening ? OPENING : OPEN_IN_PF}
                </button>
                {pfError && (
                  <p className="err-text pf-open-error" role="alert">
                    {pfError}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
        <CloseButton onClick={onClose} />
      </div>
      <div className="drawer-body">
        {a ? (
          <>
            <h3 className="drawer-h">Appointment</h3>
            <dl className="kv">
              <dt>Provider</dt>
              <dd className="row" style={{ gap: 8 }}>
                <Avatar name={a.provider.name} color={a.provider.color} size="sm" /> {a.provider.name}
              </dd>
              <dt>Location</dt>
              <dd>{a.location.name}</dd>
              <dt>Duration</dt>
              <dd>{a.durationMinutes} minutes</dd>
              <dt>Coverage</dt>
              <dd>{a.coverage.name || a.coverage.code}</dd>
            </dl>

            <h3 className="drawer-h">How it was booked</h3>
            <dl className="kv">
              <dt>Booked</dt>
              <dd>
                {fmtDateTimeOf(a.bookedAt, timeZone)} · {timeAgo(a.bookedAt)}
              </dd>
              <dt>Source</dt>
              <dd>
                <SourceName row={a} />
              </dd>
              {a.pfMessage && (
                <>
                  <dt>{PF_MESSAGE_ROW}</dt>
                  <dd className={a.pfMessage.status === 'failed' ? 'pfm-status is-failed' : 'pfm-status'}>
                    {pfMessageLabel(a.pfMessage, timeZone)}
                    {a.pfMessage.status === 'failed' && (
                      <>
                        {' · '}
                        <Link to="/clinics/$clinicId/errors" params={{ clinicId }}>
                          see Errors
                        </Link>
                      </>
                    )}
                  </dd>
                </>
              )}
            </dl>

            {hasConflicts(a) && (
              <>
                {/* Task 26 (D-83): a snapshot from booking time, only when there is one. */}
                <h3 className="drawer-h">{CONFLICTS_TITLE}</h3>
                <ul className="conflict-list">
                  {a.conflicts.map((c, i) => (
                    <li key={`${String(i)}-${c.start}`}>
                      <TriangleAlert aria-hidden />
                      <span>{conflictLine(c, timeZone)}</span>
                    </li>
                  ))}
                </ul>
                <p className="conflict-note">{CONFLICTS_NOTE}</p>
              </>
            )}

            <div className="privacy-note">
              <ShieldCheck aria-hidden />
              <span>
                The scheduler doesn’t store who the patient is. Open it in Practice Fusion to see the patient and the
                reason for the visit.
              </span>
            </div>
          </>
        ) : q.isError && !missing ? (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              void q.refetch()
            }}
          >
            Retry
          </button>
        ) : q.isPending ? (
          <span className="spinner" aria-hidden />
        ) : null}
      </div>
    </Drawer>
  )
}
